import {
  CANVAS_ELEMENT_TYPES,
  CANVAS_TOOLS,
  createCanvasElementId,
  createElementForTool,
  isBoxElement,
  normalizeBox,
} from "../nativeCanvasElements";
import {
  CLICK_ARROW_LENGTH,
  CLICK_SHAPE_SIZE,
  DEFAULT_ELEMENT_STYLE,
  MIN_FREEHAND_POINT_DISTANCE,
} from "../canvasDefaults";
import {
  ARROW_BINDING_GAP,
  findBindTarget,
  getFixedPointForBinding,
  resolveArrowElement,
} from "../geometry/arrowBinding";
import { SESSION_TYPES } from "./sessionHandles";

const createEndpointBinding = ({ bindTarget, point, towardFallback }) =>
  bindTarget
    ? {
        elementId: bindTarget.id,
        fixedPoint: getFixedPointForBinding({
          element: bindTarget,
          point,
          towardFallback,
        }),
        gap: ARROW_BINDING_GAP,
      }
    : undefined;

/**
 * Draft-element lifecycle for shape/arrow/freehand creation gestures.
 * The draft lives only on the overlay layer until commit.
 * @param {{ internals: object, setBindTarget: Function, state: object }} params
 * @returns {object}
 */
export const createDraftActions = ({ internals, setBindTarget, state }) => {
  const startCreate = (payload) => {
    const bindTarget =
      state.tool === CANVAS_TOOLS.ARROW
        ? findBindTarget({ elements: state.elements, point: payload.point })
        : null;
    const startBinding = createEndpointBinding({
      bindTarget,
      point: payload.point,
    });
    const draft = createElementForTool({
      bindings: startBinding ? { start: startBinding } : undefined,
      id: createCanvasElementId(),
      point: payload.point,
      style: DEFAULT_ELEMENT_STYLE,
      tool: state.tool,
    });
    if (!draft) return;

    state.draftElement = draft;
    state.session = {
      hasDragged: false,
      origin: payload.point,
      originScreen: payload.screenPoint,
      type: SESSION_TYPES.CREATE,
    };
    setBindTarget(bindTarget?.id ?? null);
    internals.requestRender("overlay");
  };

  const rebuildStartBinding = ({ elementsById, pointer }) => {
    const startElementId = state.draftElement.bindings?.start?.elementId;
    const startTarget = startElementId ? elementsById.get(startElementId) : null;

    // While drawing, the start pin keeps facing the pointer; it freezes on
    // release and stays fixed from then on.
    return createEndpointBinding({
      bindTarget: startTarget,
      point: pointer,
      towardFallback: pointer,
    });
  };

  const updateArrowDraft = (payload) => {
    const bindTarget = findBindTarget({
      elements: state.elements,
      point: payload.point,
    });
    const elementsById = new Map(
      state.elements.map((element) => [element.id, element])
    );
    const startPoint = {
      x: state.draftElement.x1,
      y: state.draftElement.y1,
    };
    const draft = {
      ...state.draftElement,
      bindings: {
        end: createEndpointBinding({
          bindTarget,
          point: payload.point,
          towardFallback: startPoint,
        }),
        start: rebuildStartBinding({ elementsById, pointer: payload.point }),
      },
      x2: payload.point.x,
      y2: payload.point.y,
    };
    state.draftElement = resolveArrowElement({
      arrow: draft,
      resolveElementById: (elementId) => elementsById.get(elementId),
    });
    setBindTarget(bindTarget?.id ?? null);
  };

  const appendFreehandPoint = (payload) => {
    const points = state.draftElement.points;
    const lastPoint = points[points.length - 1];
    const distance = Math.hypot(
      payload.point.x - lastPoint.x,
      payload.point.y - lastPoint.y
    );
    if (distance >= MIN_FREEHAND_POINT_DISTANCE) points.push(payload.point);
  };

  const updateDraft = (payload) => {
    const draftType = state.draftElement.type;
    if (draftType === CANVAS_ELEMENT_TYPES.ARROW) updateArrowDraft(payload);
    else if (draftType === CANVAS_ELEMENT_TYPES.PATH) appendFreehandPoint(payload);
    else {
      state.draftElement = {
        ...state.draftElement,
        ...normalizeBox({ end: payload.point, start: state.session.origin }),
      };
    }
    internals.requestRender("overlay");
  };

  const finalizeClickDraft = (draft) => {
    if (draft.type === CANVAS_ELEMENT_TYPES.ARROW) {
      return { ...draft, x2: draft.x1 + CLICK_ARROW_LENGTH, y2: draft.y1 };
    }
    if (isBoxElement(draft)) return { ...draft, ...CLICK_SHAPE_SIZE };

    return draft;
  };

  const finishCreate = (session) => {
    const draft = session.hasDragged
      ? state.draftElement
      : finalizeClickDraft(state.draftElement);
    state.draftElement = null;
    setBindTarget(null);
    internals.commitElements([...state.elements, draft], {
      selectIds: new Set([draft.id]),
    });
    internals.handleToolUsed();
  };

  return { finishCreate, startCreate, updateDraft };
};
